import FingerprintJS, { Agent } from '@fingerprintjs/fingerprintjs';
import {
  ApiSDK,
  TApiSDK,
  type TDeviceInfoService,
  type TLanguageService,
  type TRequestIdService,
} from '@poseidon-city/api-sdk';
import { generateId, isDefined, isNil } from '@poseidon-city/utils';
import { SHA256 } from 'crypto-js';
import { detectIncognito } from 'detectincognitojs';
import React, { Context, createContext, useContext } from 'react';

import { config } from '../config';

const getHost = async (): Promise<string> => {
  if (typeof window !== 'undefined') {
    const isDev =
      process.env.NODE_ENV === 'development' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (isDev) {
      return config.get('api.url');
    }

    return Promise.resolve(window.location.origin);
  }

  return config.get('api.url');
};

function DeviceInfoService(): TDeviceInfoService {
  const agentPromise: Promise<Agent> = FingerprintJS.load();

  return {
    async getIncognitoMode(): Promise<boolean> {
      const oldIncognito = localStorage.getItem('incognito');

      if (isDefined(oldIncognito)) {
        return oldIncognito === 'true';
      }

      const { isPrivate } = await detectIncognito();

      localStorage.setItem('incognito', isPrivate.toString());

      console.log('%c✔ incognito mode', 'color:#4cca9f', isPrivate);

      return isPrivate;
    },

    async getFingerprintId(): Promise<string> {
      const oldFingerprintId = localStorage.getItem('fingerprintId');

      if (isDefined(oldFingerprintId)) {
        return oldFingerprintId;
      }

      const agent = await agentPromise;
      const result = await agent.get();

      const fingerprintId = SHA256(result.visitorId).toString();

      localStorage.setItem('fingerprintId', fingerprintId);

      console.log('%c✔ fingerprint unique identifier', 'color:#4cca9f', fingerprintId);

      return fingerprintId;
    },

    getPoseidonId(): string {
      const oldPoseidonId = localStorage.getItem('poseidonId');

      if (isDefined(oldPoseidonId)) {
        return oldPoseidonId;
      }

      const canvas = document.createElement('canvas');
      canvas.width = 500;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');

      if (isNil(ctx)) {
        return 'N/A';
      }

      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, 'magenta');
      gradient.addColorStop(0.5, 'blue');
      gradient.addColorStop(1, 'red');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, 100);

      // Text
      ctx.font = '24px Arial';
      ctx.fillStyle = 'yellow';
      ctx.fillText('Poseidon City 2.0 <fingerprint> 💀', 65, 60);

      // Text
      ctx.font = '24px Arial';
      ctx.fillStyle = 'green';
      ctx.fillText('Poseidon City 2.0 <fingerprint> 👍', 65, 130);

      // Kruhy s různou průhledností
      for (let i = 0; i < 10; i++) {
        ctx.beginPath();
        ctx.arc(35 + i * 47, 170, 20, 0, 2 * Math.PI);
        ctx.fillStyle = `rgba(${255 - i * 25}, ${i * 25}, 125, 0.${i + 1})`;
        ctx.fill();
      }

      const dataUrl = canvas.toDataURL();
      const poseidonId = SHA256(dataUrl).toString();

      localStorage.setItem('poseidonId', poseidonId);

      console.log('%c✔ poseidon unique identifier', 'color:#4cca9f', poseidonId);

      return poseidonId;
    },
  };
}

function LanguageService(): TLanguageService {
  return {
    getSelectedLanguage(): string {
      return localStorage.getItem('lng') ?? 'en';
    },
  };
}

function RequestIdService(): TRequestIdService {
  return {
    getUniqueId: generateId,
  };
}

const sdk: TApiSDK = ApiSDK(
  {
    host: await getHost(),
    //host: config.get('api.url'),
    pathPrefix: config.get('api.pathPrefix'),
    clientId: config.get('client.id'),
    clientSecret: config.get('client.secret'),
  },
  DeviceInfoService(),
  RequestIdService(),
  LanguageService()
);

const ApiSDKContext: Context<TApiSDK> = createContext<TApiSDK>(sdk);

type TApiSDKContextProviderProps = {
  children: React.ReactNode;
};

function ApiSDKContextProvider({ children }: TApiSDKContextProviderProps): JSX.Element {
  return <ApiSDKContext.Provider value={sdk}>{children}</ApiSDKContext.Provider>;
}

function useApiSDK(): TApiSDK {
  return useContext(ApiSDKContext);
}

export { ApiSDKContextProvider, useApiSDK };
